<template>
    <survey-details-dashboard :survey="surveyModel" :surveyReport="surveyReport"></survey-details-dashboard>
    <div class="p-6 shadow-md rounded-md mb-15">
        <div class="flex justify-between mb-6">
            <h2 class="font-bold text-3xl dark:text-gray-200 pr-6">Statistics {{ surveyModel.name }}</h2>
            <jet-button @click.prevent="closeSurvey"
                        v-if="canBeClosed"
                        :bg-dark="true"
            >
                Close Survey
            </jet-button>
        </div>

        <div class="flex justify-between items-center">
            <h4 class="font-bold text-2xl">Respondents</h4>
            <jet-button @click.prevent="sendInvites"
                        v-if="canBeClosed"
                        :bg-dark="true"
            >
                Resend invites to all who didn't complete the survey
                <svg class="w-3 h-3 ml-2" aria-hidden="true" focusable="false" data-prefix="fas"
                     data-icon="share" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">
                    <path fill="white"
                          d="M503.691 189.836L327.687 37.851C312.281 24.546 288 35.347 288 56.015v80.053C127.371 137.907 0 170.1 0 322.326c0 61.441 39.581 122.309 83.333 154.132 13.653 9.931 33.111-2.533 28.077-18.631C66.066 312.814 132.917 274.316 288 272.085V360c0 20.7 24.3 31.453 39.687 18.164l176.004-152c11.071-9.562 11.086-26.753 0-36.328z"></path>
                </svg>
            </jet-button>
        </div>

        <div class="w-full mb-8 mt-3 overflow-hidden rounded-lg shadow-xs">
            <survey-respondents-table :paginatedRespondents="paginatedRespondents"
                                      :canInviteRespondents="canBeClosed"
                                      :survey="{ id: surveyModel.id, tenant_id: surveyModel.tenant_id }"></survey-respondents-table>
        </div>

        <div class="flex mb-6 items-center min-w-0 p-4 bg-white rounded-lg shadow-xs dark:bg-gray-600">
            <p class="mr-4 font-bold text-2xl">Survey Average Score: </p>
            <span
                class="px-2 py-1 font-semibold leading-tight rounded-full"
                :style="{
                    backgroundColor: surveyReport.scoreRatingDetails.bg_color,
                    color: surveyReport.scoreRatingDetails.text_color,
                }"
            >
                <strong class="font-extrabold mr-2">{{ surveyReport.avgScore }}</strong> {{ surveyReport.scoreRatingDetails.label }}
            </span>
        </div>

        <div class="border rounded-md">
            <collapsable>
                <template v-slot:trigger>
                    <button
                        class="w-full p-3 text-xl font-bold rounded-md bg-white shadow-xs dark:bg-gray-600 flex items-center focus:outline-none">
                        Users Filtering
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ml-4" fill="none" viewBox="0 0 24 24"
                             stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"/>
                        </svg>
                    </button>
                </template>
                <template v-slot:content>
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xs px-4 py-3 sm:p-6">
                        <div class="grid md:grid-cols-4 gap-2">
                            <label class="block text-sm" v-for="profileField in surveyModel.profile_fields"
                                   :key="'profile-field-' + profileField.id">
                                <span class="text-gray-700 dark:text-gray-400">{{ profileField.name }}</span>
                                <select v-model="filterValues[profileField.id]"
                                        class="block w-full mt-1 text-sm dark:text-gray-300 dark:border-gray-600 dark:bg-gray-700 form-select focus:border-purple-400 focus:outline-none focus:shadow-outline-purple dark:focus:shadow-outline-gray">
                                    <option :value="undefined">Select an option</option>
                                    <option v-for="profileFieldValue in profileField.values"
                                            :key="'profile-field-value-' + profileFieldValue.id"
                                            :value="profileFieldValue.id">{{
                                            profileFieldValue.value
                                        }}
                                    </option>
                                </select>
                            </label>
                        </div>
                    </div>
                </template>
            </collapsable>
        </div>

        <div class="w-full my-8 overflow-hidden rounded-lg border">
            <div class="w-full overflow-x-auto">
                <survey-section-details v-for="section in detailedSections" :key="'detailed-section-' + section.id"
                                        :should-render-charts="!surveyModel.isClosed && surveySettings.live_analytics"
                                        :section="section"></survey-section-details>
            </div>
        </div>
    </div>
</template>

<script>
import AppLayout from "@/Layouts/AppLayout";
import SurveyDetailsDashboard from "@/Components/Survey/SurveyDetailsDashboard";
import SurveySectionDetails from "@/Components/Survey/SurveySectionDetails";
import SurveyRespondentsTable from "@/Components/Survey/SurveyRespondentsTable";
import Collapsable from '@/Components/Collapsable';
import GenericHeader from "@/Components/GenericHeader";
import JetButton from "@/Jetstream/Button";

import {computed, inject, ref, watch} from 'vue'
import {Inertia} from "@inertiajs/inertia";
import {usePage} from "@inertiajs/inertia-vue3";

export default {
    name: "Show",
    components: {
        AppLayout,
        Collapsable,
        SurveyDetailsDashboard,
        SurveyRespondentsTable,
        SurveySectionDetails,
        JetButton
    },
    props: {
        surveyModel: {
            type: Object,
            required: true,
        },
        surveyReport: {
            type: Object,
            required: true,
        },
        surveySettings: {
            type: Object,
            required: true,
        },
        paginatedRespondents: {
            type: Object,
            required: true,
        },
        filterValues: {
            type: Object,
            required: true,
        },
        filteredSurveyModel: {
            type: Object,
            required: true,
        },
        filteredSurveyReport: {
            type: Object,
            required: true,
        },
    },
    layout: (h, page) => {
        return h(AppLayout, {
            title: "Survey " + page.props.surveyModel.name,
        }, {
            header: () => h(GenericHeader, {
                text: page.props.surveyModel.name,
            }),
            default: () => page,
        });
    },
    setup(props) {
        const tenant = usePage().props.value.tenant;
        const canManageSurvey = Boolean(usePage().props.value.allUserPermissions
            && (usePage().props.value.allUserPermissions.indexOf('manage surveys') !== -1) && tenant);
        const canBeClosed = Boolean(canManageSurvey && !props.surveyModel.isClosed && props.surveyModel.isFullyCreated && tenant);

        const isComplete = (respondent) => respondent.finished_at;
        const isIncomplete = (respondent) => respondent.finished_at === null && respondent.started_at;
        const isNotStarted = (respondent) => respondent.started_at === null;

        const detailedSections = computed(() => {
            return props.filteredSurveyModel.sections.map((section) => {
                return {
                    id: section.id,
                    name: section.name,
                    avgScore: props.filteredSurveyReport.sections[section.id].avgScore,
                    scoreRatingLabel: props.filteredSurveyReport.sections[section.id].scoreRatingDetails.label,
                    bgColor: props.filteredSurveyReport.sections[section.id].scoreRatingDetails.bg_color,
                    textColor: props.filteredSurveyReport.sections[section.id].scoreRatingDetails.text_color,
                    questions: section.questions.map((question) => {
                        return {
                            id: question.id,
                            content: question.content,
                            allow_response_comment: question.allow_response_comment,
                            response_comment_label: question.response_comment_label,
                            comments: question.survey_responses.filter(item => item.comment !== null).map((item) => {
                                return {
                                    comment: item.comment,
                                };
                            }),
                            avgScore: props.filteredSurveyReport.sections[section.id].questions[question.id].avgScore,
                            label: props.filteredSurveyReport.sections[section.id].questions[question.id].scoreRatingDetails.label,
                            bgColor: props.filteredSurveyReport.sections[section.id].questions[question.id].scoreRatingDetails.bg_color,
                            textColor: props.filteredSurveyReport.sections[section.id].questions[question.id].scoreRatingDetails.text_color,
                            answers: question.answers,
                            survey_responses: question.survey_responses,
                        };
                    })
                }
            });
        });

        const $swal = inject('$swal');
        const sendInvites = () => {
            $swal({
                title: 'Are you sure?',
                text: "All respondents who didn't completed the survey will be notified!",
                icon: 'info',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, send it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.put(route('tenant.survey.remind-respondents', {
                        survey: props.surveyModel.id,
                        tenant: props.surveyModel.tenant_id,
                    }), {}, {
                        preserveScroll: true,
                    });
                }
            });
        };

        const closeSurvey = () => {
            $swal({
                title: 'Are you sure?',
                text: "You will close this survey!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Yes, close it!'
            }).then((result) => {
                if (result.isConfirmed) {
                    Inertia.put(route('tenant.survey.close', {
                        survey: props.surveyModel.id,
                        tenant: props.surveyModel.tenant_id,
                    }), {}, {
                        preserveScroll: true,
                    });
                }
            });
        };

        const filterData = () => {
            Inertia.visit(route('tenant.survey.show', {
                ...filterValues.value,
                survey: props.surveyModel.id,
            }), {
                method: 'get',
                preserveScroll: true,
                preserveState: true,
                only: [
                    'filteredSurveyModel',
                    'filteredSurveyReport',
                ],
            });
        };

        const filterValues = ref(props.filterValues);
        watch(filterValues.value, filterData);

        return {
            detailedSections,
            canBeClosed,
            isNotStarted,
            isIncomplete,
            isComplete,
            sendInvites,
            closeSurvey,
            filterValues,
        };
    }
}
</script>

<style scoped>

</style>
